<template>
    <Dialog
        :visible="visible"
        :header="`${t('maintenance.scheduleFor')} ${resourceName} (${resourceType})`"
        modal
        :draggable="false"
        contentClass="!p-0 overflow-hidden rounded-lg"
        :style="{ width: '50rem' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        @update:visible="$emit('update:visible', $event)"
    >
        <div class="px-4 pb-4">
            <!-- Add Slot Button -->
            <div class="flex justify-end items-center mb-3">
                <Button icon="fa-solid fa-plus" :label="t('maintenance.addSlot')" @click="$emit('addSlot')" />
            </div>

            <!-- Empty State -->
            <div v-if="slots.length === 0" class="text-center py-12 rounded-lg border-2 border-dashed border-surface-300 bg-surface-50">
                <i class="fa-solid fa-calendar-plus text-5xl text-gray-400 mb-3"></i>
                <p class="font-semibold mb-1">{{ t('maintenance.noSlots') }}</p>
                <p class="">{{ t('maintenance.noSlotsDescription') }}</p>
            </div>

            <!-- Slots -->
            <div v-else class="space-y-3 overflow-y-auto max-h-[60vh]">
                <div v-for="(slot, index) in slots" :key="index" class="group relative rounded-lg border bg-white transition-all duration-200 border-surface-200 hover:border-primary-200">
                    <div class="px-3 py-2 space-y-3">
                        <!-- Header -->
                        <div class="flex items-center justify-between">
                            <Tag :value="slot.title && slot.title.trim() ? slot.title : t('maintenance.untitledSlot')" severity="info" icon="fa-solid fa-wrench" />
                            <Button icon="fa-regular fa-trash-can" @click="$emit('removeSlot', index)" text rounded severity="danger" class="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>

                        <div class="space-y-3">
                            <!-- Title -->
                            <div>
                                <label class="block font-semibold mb-1.5"> {{ t('common.title') }} <span class="text-red-500">*</span> </label>
                                <InputText :modelValue="slot.title" @update:modelValue="$emit('updateSlot', index, 'title', $event)" :placeholder="t('maintenance.form.titlePlaceholder')" class="w-full" @blur="markTouched(index, 'title')" />
                                <small v-if="isFieldInvalid(slot, index, 'title')" class="text-red-500 text-xs">
                                    {{ t('maintenance.validation.titleRequired') }}
                                </small>
                            </div>

                            <!-- Day of Week and Time Range -->
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label class="block font-semibold mb-1.5"> {{ t('maintenance.form.dayOfWeek') }} <span class="text-red-500">*</span> </label>
                                    <Select
                                        :modelValue="slot.dayOfWeek"
                                        @update:modelValue="$emit('updateSlot', index, 'dayOfWeek', $event)"
                                        :options="daysOfWeek"
                                        optionLabel="label"
                                        optionValue="value"
                                        class="w-full"
                                        :placeholder="t('maintenance.form.selectDay')"
                                        @change="markTouched(index, 'dayOfWeek')"
                                        @blur="markTouched(index, 'dayOfWeek')"
                                    />
                                    <small v-if="isFieldInvalid(slot, index, 'dayOfWeek')" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.dayRequired') }}
                                    </small>
                                </div>

                                <div class="md:col-span-2">
                                    <label class="block font-semibold mb-1.5"> {{ t('maintenance.form.timeRange') }} <span class="text-red-500">*</span> </label>
                                    <div class="flex flex-row items-center gap-4">
                                        <div class="flex flex-col gap-2 flex-1">
                                            <DatePicker
                                                :ref="
                                                    (el: any) => {
                                                        if (el) timePickerRefs[`start-${index}`] = el;
                                                    }
                                                "
                                                timeOnly
                                                :stepMinute="15"
                                                :modelValue="timeStringToDate(slot.startTime)"
                                                @update:modelValue="
                                                    (value) => {
                                                        $emit('updateSlot', index, 'startTime', dateToTimeString(value as Date));
                                                        markTouched(index, 'startTime');
                                                    }
                                                "
                                                @hide="
                                                    () => {
                                                        const rounded = roundToNearest15Minutes(timeStringToDate(slot.startTime));
                                                        $emit('updateSlot', index, 'startTime', dateToTimeString(rounded));
                                                    }
                                                "
                                                @show="
                                                    () =>
                                                        handleTimePickerShow(`start-${index}`, slot.startTime, (v) => {
                                                            emit('updateSlot', index, 'startTime', v);
                                                            markTouched(index, 'startTime');
                                                        })
                                                "
                                                class="w-full"
                                            >
                                                <template #footer>
                                                    <div class="flex justify-end pt-2 border-t">
                                                        <Button
                                                            :label="t('common.apply')"
                                                            size="small"
                                                            severity="success"
                                                            @click="
                                                                confirmTimePicker(`start-${index}`, (v) => {
                                                                    emit('updateSlot', index, 'startTime', v);
                                                                    markTouched(index, 'startTime');
                                                                })
                                                            "
                                                        />
                                                    </div>
                                                </template>
                                            </DatePicker>
                                        </div>
                                        <span class="mx-1 font-medium">-</span>
                                        <div class="flex flex-col gap-2 flex-1">
                                            <DatePicker
                                                :ref="
                                                    (el: any) => {
                                                        if (el) timePickerRefs[`end-${index}`] = el;
                                                    }
                                                "
                                                timeOnly
                                                :stepMinute="15"
                                                :modelValue="timeStringToDate(slot.endTime)"
                                                @update:modelValue="
                                                    (value) => {
                                                        $emit('updateSlot', index, 'endTime', dateToTimeString(value as Date));
                                                        markTouched(index, 'endTime');
                                                    }
                                                "
                                                @hide="
                                                    () => {
                                                        const rounded = roundToNearest15Minutes(timeStringToDate(slot.endTime));
                                                        $emit('updateSlot', index, 'endTime', dateToTimeString(rounded));
                                                    }
                                                "
                                                @show="
                                                    () =>
                                                        handleTimePickerShow(`end-${index}`, slot.endTime, (v) => {
                                                            emit('updateSlot', index, 'endTime', v);
                                                            markTouched(index, 'endTime');
                                                        })
                                                "
                                                class="w-full"
                                            >
                                                <template #footer>
                                                    <div class="flex justify-end pt-2 border-t">
                                                        <Button
                                                            :label="t('common.apply')"
                                                            size="small"
                                                            severity="success"
                                                            @click="
                                                                confirmTimePicker(`end-${index}`, (v) => {
                                                                    emit('updateSlot', index, 'endTime', v);
                                                                    markTouched(index, 'endTime');
                                                                })
                                                            "
                                                        />
                                                    </div>
                                                </template>
                                            </DatePicker>
                                        </div>
                                        <div class="flex items-center gap-1.5 ml-4">
                                            <Checkbox v-model="allDaySlots[index]" :binary="true" @change="handleAllDayChange(index)" :inputId="`allDay-${index}`" />
                                            <label :for="`allDay-${index}`" class="font-medium cursor-pointer whitespace-nowrap">
                                                {{ t('maintenance.form.allDay') }}
                                            </label>
                                        </div>
                                    </div>
                                    <small v-if="isFieldInvalid(slot, index, 'startTime')" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.startTimeRequired') }}
                                    </small>
                                    <small v-else-if="isFieldInvalid(slot, index, 'endTime')" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.endTimeRequired') }}
                                    </small>
                                    <small v-else-if="isTimeOrderInvalid(slot)" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.endAfterStart') }}
                                    </small>
                                </div>
                            </div>

                            <!-- Valid Period -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label class="block font-semibold mb-1.5">
                                        {{ t('maintenance.form.validFrom') }}
                                    </label>
                                    <DatePicker
                                        :modelValue="slot.validFromDate"
                                        @update:modelValue="$emit('updateSlot', index, 'validFromDate', $event)"
                                        :placeholder="t('maintenance.form.validFromPlaceholder')"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        iconDisplay="input"
                                        :showButtonBar="true"
                                        class="w-full"
                                    >
                                        <template #inputicon="slotProps">
                                            <i class="fa-regular fa-calendar"></i>
                                        </template>
                                    </DatePicker>
                                </div>

                                <div>
                                    <label class="block font-semibold mb-1.5">
                                        {{ t('maintenance.form.validUntil') }}
                                    </label>
                                    <DatePicker
                                        :modelValue="slot.validUntilDate"
                                        @update:modelValue="$emit('updateSlot', index, 'validUntilDate', $event)"
                                        :placeholder="t('maintenance.form.validUntilPlaceholder')"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        iconDisplay="input"
                                        :showButtonBar="true"
                                        class="w-full"
                                    >
                                        <template #inputicon="slotProps">
                                            <i class="fa-regular fa-calendar"></i>
                                        </template>
                                    </DatePicker>
                                </div>
                            </div>

                            <!-- Blocking Mode (D-05: absent, not disabled, on a capacity-1 or capacity-0 resource) -->
                            <div v-if="resourceCapacity > 1">
                                <label :id="`blockingMode-label-${index}`" class="block font-semibold mb-1.5">
                                    {{ t('maintenance.form.blockingMode') }}
                                </label>
                                <SelectButton
                                    :modelValue="slot.blockingMode"
                                    @update:modelValue="onBlockingModeChange(index, $event)"
                                    :options="blockingModeOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    :allowEmpty="false"
                                    :aria-labelledby="`blockingMode-label-${index}`"
                                />

                                <div v-if="slot.blockingMode === BlockingMode.PartiallyBlocking" class="mt-3">
                                    <label :for="`reducedCapacity-${index}`" class="block font-semibold mb-1.5"> {{ t('maintenance.form.reducedCapacity') }} <span class="text-red-500">*</span> </label>
                                    <InputNumber
                                        :modelValue="slot.reducedCapacity"
                                        @update:modelValue="$emit('updateSlot', index, 'reducedCapacity', $event)"
                                        :min="1"
                                        :max="resourceCapacity"
                                        :useGrouping="false"
                                        showButtons
                                        fluid
                                        :inputId="`reducedCapacity-${index}`"
                                        @blur="markTouched(index, 'reducedCapacity')"
                                    />
                                    <small class="text-xs block">{{ t('maintenance.form.reducedCapacityHint', { max: resourceCapacity }) }}</small>
                                    <small v-if="isFieldInvalid(slot, index, 'reducedCapacity') && isReducedCapacityMissing(slot)" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.reducedCapacityRequired') }}
                                    </small>
                                    <small v-else-if="isFieldInvalid(slot, index, 'reducedCapacity')" class="text-red-500 text-xs">
                                        {{ t('maintenance.validation.reducedCapacityRange', { max: resourceCapacity }) }}
                                    </small>
                                </div>
                            </div>

                            <!-- Description -->
                            <div>
                                <label class="block font-semibold mb-1.5">
                                    {{ t('common.description') }}
                                </label>
                                <Textarea :modelValue="slot.description" @update:modelValue="$emit('updateSlot', index, 'description', $event)" rows="2" :placeholder="t('maintenance.form.descriptionPlaceholder')" class="w-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Validation Error (MAINT-05 conflict list rides inside the same single banner) -->
            <Message v-if="validationError" severity="error" :closable="false">
                <div>{{ validationError }}</div>
                <ul v-if="conflicts.length > 0" class="mt-2 space-y-1 text-sm list-none p-0 m-0">
                    <li v-for="(conflict, conflictIndex) in conflicts.slice(0, CONFLICT_LIST_CAP)" :key="conflictIndex">
                        {{ t('maintenance.conflicts.row', { date: conflict.date, start: conflict.startTime, end: conflict.endTime, attendees: conflict.attendees, cap: conflict.cap }) }}
                    </li>
                    <li v-if="conflicts.length > CONFLICT_LIST_CAP" class="italic">
                        {{ t('maintenance.conflicts.more', conflicts.length - CONFLICT_LIST_CAP, { count: conflicts.length - CONFLICT_LIST_CAP }) }}
                    </li>
                </ul>
            </Message>
        </div>

        <div class="flex justify-end p-3 border-t bg-gray-50">
            <Button :label="t('common.save')" icon="fa-solid fa-check" severity="success" @click="$emit('save')" :loading="saving" />
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { CONFLICT_LIST_CAP } from '@/composables/useMaintenanceScheduleDialog';
import type { MaintenanceSlotForm } from '@/composables/useMaintenancesPage';
import { BlockingMode } from '@/lib/types/maintenance';
import { ceilToNext15Minutes, dateToTimeString, DAYS_OF_WEEK, roundToNearest15Minutes, timeStringToDate } from '@/utils/date';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

// Dynamic refs for time pickers
const timePickerRefs = ref<Record<string, any>>({});

/**
 * Read the currently displayed time from a DatePicker's internal state,
 * round to nearest 15 min, apply it via callback, then close the popup.
 */
function confirmTimePicker(key: string, applyValue: (timeStr: string) => void) {
    const picker = timePickerRefs.value[key];
    if (picker) {
        const hour = picker.currentHour ?? 0;
        const minute = picker.currentMinute ?? 0;
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        const rounded = roundToNearest15Minutes(date);
        applyValue(dateToTimeString(rounded));
        picker.overlayVisible = false;
    }
}

/**
 * When the popup opens and the input is empty, populate with the next 15-min step.
 */
function handleTimePickerShow(key: string, currentValue: string | null | undefined, applyValue: (timeStr: string) => void) {
    if (!currentValue || !currentValue.trim()) {
        const now = new Date();
        const next = ceilToNext15Minutes(now);
        const timeStr = dateToTimeString(next);
        applyValue(timeStr);
    }
}

type SlotField = 'title' | 'dayOfWeek' | 'startTime' | 'endTime' | 'reducedCapacity';

// Track touched state per slot (by index)
const slotsTouched = reactive<Map<number, Record<SlotField, boolean>>>(new Map());

// Get or create touched state for a slot
function getSlotTouched(index: number) {
    if (!slotsTouched.has(index)) {
        slotsTouched.set(index, { title: false, dayOfWeek: false, startTime: false, endTime: false, reducedCapacity: false });
    }
    return slotsTouched.get(index)!;
}

// Mark a slot field as touched
function markTouched(index: number, field: SlotField) {
    const touched = getSlotTouched(index);
    touched[field] = true;
}

// Partial mode with no capacity typed yet
function isReducedCapacityMissing(slot: MaintenanceSlotForm): boolean {
    if (slot.blockingMode !== BlockingMode.PartiallyBlocking) return false;
    return slot.reducedCapacity == null || typeof slot.reducedCapacity !== 'number' || Number.isNaN(slot.reducedCapacity);
}

// Partial mode with a capacity outside [1, resourceCapacity] — reachable from stored data even though the spinner
// itself is bounded, because the resource may have shrunk after the maintenance was saved (D-08).
function isReducedCapacityOutOfRange(slot: MaintenanceSlotForm): boolean {
    if (slot.blockingMode !== BlockingMode.PartiallyBlocking) return false;
    if (isReducedCapacityMissing(slot)) return false;
    return (slot.reducedCapacity as number) < 1 || (slot.reducedCapacity as number) > props.resourceCapacity;
}

// Check if a slot field is invalid
function isFieldInvalid(slot: MaintenanceSlotForm, index: number, field: SlotField): boolean {
    const touched = slotsTouched.get(index);
    if (!touched || !touched[field]) return false;

    if (field === 'title') {
        return !slot.title || slot.title.trim() === '';
    }
    if (field === 'dayOfWeek') {
        return !slot.dayOfWeek;
    }
    if (field === 'startTime') {
        return !slot.startTime || slot.startTime.trim() === '';
    }
    if (field === 'endTime') {
        return !slot.endTime || slot.endTime.trim() === '';
    }
    if (field === 'reducedCapacity') {
        return isReducedCapacityMissing(slot) || isReducedCapacityOutOfRange(slot);
    }
    return false;
}

// Check if start time is before end time
function isTimeOrderInvalid(slot: MaintenanceSlotForm): boolean {
    if (!slot.startTime || !slot.endTime) return false;

    const startParts = slot.startTime.split(':').map(Number);
    const endParts = slot.endTime.split(':').map(Number);

    if (startParts.length !== 2 || endParts.length !== 2) return false;

    const startMinutes = startParts[0] * 60 + startParts[1];
    const endMinutes = endParts[0] * 60 + endParts[1];

    return startMinutes >= endMinutes;
}

interface Props {
    visible: boolean;
    resourceName: string;
    resourceType: string;
    resourceCapacity: number;
    slots: MaintenanceSlotForm[];
    saving: boolean;
    validationError: string | null;
    conflicts: MaintenanceConflictRow[];
}

interface Emits {
    (e: 'update:visible', value: boolean): void;
    (e: 'addSlot'): void;
    (e: 'removeSlot', index: number): void;
    (e: 'updateSlot', index: number, field: string, value: any): void;
    (e: 'setBlockingMode', index: number, mode: BlockingMode): void;
    (e: 'save'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const { t } = useI18n();

const allDaySlots = ref<Record<number, boolean>>({});

function handleAllDayChange(index: number) {
    if (allDaySlots.value[index]) {
        emit('updateSlot', index, 'startTime', '00:00');
        emit('updateSlot', index, 'endTime', '23:59');
    }
}

// Watch for slots changes and initialize all-day checkboxes based on actual time values
watch(
    () => props.slots,
    (newSlots) => {
        if (newSlots) {
            const newAllDaySlots: Record<number, boolean> = {};
            newSlots.forEach((slot, index) => {
                // Check if this slot is actually an all-day slot (00:00 - 23:59)
                newAllDaySlots[index] = slot.startTime === '00:00' && slot.endTime === '23:59';
            });
            allDaySlots.value = newAllDaySlots;
        }
    },
    { immediate: true, deep: true }
);

// Days of week for dropdown, using common utility
const daysOfWeek = computed(() =>
    DAYS_OF_WEEK.map((day) => ({
        label: t(`days.${day.toLowerCase()}`),
        value: day
    }))
);

// Computed so the two labels re-translate on a locale change
const blockingModeOptions = computed(() => [
    { label: t('maintenance.form.blockingModeFully'), value: BlockingMode.FullyBlocking },
    { label: t('maintenance.form.blockingModePartial'), value: BlockingMode.PartiallyBlocking }
]);

// The mode switch clears or prefills the capacity as a side effect, so it goes to its own handler in the
// composable rather than through the generic updateSlot.
function onBlockingModeChange(index: number, mode: BlockingMode | null) {
    if (!mode) return;
    emit('setBlockingMode', index, mode);
}

// D-08 surfaced: when the dialog opens on a maintenance whose stored reducedCapacity exceeds the resource's
// current capacity, that field shows its range error immediately — the operator sees why the save will be
// refused instead of watching a silent clamp. Every other field keeps its untouched-until-interacted behaviour,
// which is why the map is cleared first.
watch(
    () => props.visible,
    (isVisible) => {
        if (!isVisible) return;

        slotsTouched.clear();
        props.slots.forEach((slot, index) => {
            if (isReducedCapacityOutOfRange(slot)) {
                markTouched(index, 'reducedCapacity');
            }
        });
    },
    { immediate: true }
);
</script>
